import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import GameCard from '@/components/feature/GameCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { games } from '@/mocks/games';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = getArticleById(articleId);
  const relatedArticles = getRelatedArticles(articleId, 3);
  const relatedGames = games.slice(0, 4);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [articleId]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: 'Galgame资源网站PC',
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: article.category, path: '/category/gonglve' },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} — Galgame资源网站PC`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${articleId}`}
        ogImage={article.coverImage}
        ogType="article"
        articleData={{
          publishedTime: article.date,
          author: article.author,
        }}
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            <article className="flex-1 min-w-0">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-4">
                  {article.category}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount >= 10000
                      ? `${(article.readCount / 10000).toFixed(1)}万`
                      : article.readCount}{' '}
                    阅读
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-background-200/70 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8 bg-background-200/50">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-base md:prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-li:text-foreground-700 prose-strong:text-foreground-900 prose-a:text-primary-600"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            <aside className="lg:w-[340px] flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-8">
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                    相关游戏推荐
                  </h3>
                  <div className="space-y-4" data-product-shop>
                    {relatedGames.map((game) => (
                      <Link
                        key={game.id}
                        to={`/article/${game.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-14 rounded-md overflow-hidden flex-shrink-0 bg-background-200/50">
                          <img
                            src={game.image}
                            alt={game.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {game.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
                            <span className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-accent-500 text-xs"></i>
                              {game.rating}
                            </span>
                            <span>{game.size}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                    相关文章
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="block group cursor-pointer"
                      >
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 line-clamp-1">
                          {ra.excerpt}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}